import { CRM_INTEGRATION_SEND_FAILURE_MESSAGE } from './crmIntegrationApiErrors'
import type { CrmIntegrationPendingMessage } from '../types/crm-integration-message.types'
import type {
  CrmIntegrationMessageDisplay,
  CrmIntegrationMessageView,
} from '../types/crm-integration-message.types'
import type {
  CrmIntegrationAttachment,
  CrmIntegrationOutgoingAttachment,
} from '../types/crm-integration-chat.types'

export const mapServerMessageToDisplay = (
  message: CrmIntegrationMessageView,
): CrmIntegrationMessageDisplay => {
  if (message.delivery.status === 'failed') {
    return {
      ...message,
      sendState: 'error',
      sendError: message.delivery.error ?? CRM_INTEGRATION_SEND_FAILURE_MESSAGE,
    }
  }

  // pending = ещё у провайдера; в UI как «отправляется», не как уже доставлено.
  if (message.delivery.status === 'pending') {
    return {
      ...message,
      sendState: 'sending',
    }
  }

  return message
}

export const pendingToDisplayMessage = (
  pending: CrmIntegrationPendingMessage,
): CrmIntegrationMessageDisplay => {
  const attachments: CrmIntegrationAttachment[] = []

  if (pending.pendingImagePreviewUrl) {
    attachments.push({
      kind: 'image',
      url: pending.pendingImagePreviewUrl,
      preview_url: pending.pendingImagePreviewUrl,
      name: pending.attachmentFileName ?? null,
      mime: null,
      size: null,
      duration: null,
      provider: 'telegram',
    })
  } else if (pending.pendingVideoPreviewUrl) {
    attachments.push({
      kind: 'video',
      url: pending.pendingVideoPreviewUrl,
      preview_url: null,
      name: pending.attachmentFileName ?? null,
      mime: pending.uploadedOutgoingAttachments?.[0]?.mime ?? null,
      size: pending.uploadedOutgoingAttachments?.[0]?.size ?? null,
      duration: pending.uploadedOutgoingAttachments?.[0]?.duration ?? null,
      provider: 'telegram',
    })
  } else if (pending.pendingVoicePreviewUrl) {
    attachments.push({
      kind: 'voice',
      url: pending.pendingVoicePreviewUrl,
      preview_url: null,
      name: pending.attachmentFileName ?? null,
      mime: pending.uploadedOutgoingAttachments?.[0]?.mime ?? null,
      size: pending.uploadedOutgoingAttachments?.[0]?.size ?? null,
      duration:
        pending.pendingVoiceDurationSec ??
        pending.uploadedOutgoingAttachments?.[0]?.duration ??
        null,
      provider: 'telegram',
    })
  } else if (pending.attachmentFileName) {
    const uploadedKind = pending.uploadedOutgoingAttachments?.[0]?.kind ?? 'file'
    attachments.push({
      kind:
        uploadedKind === 'image' || uploadedKind === 'video'
          ? uploadedKind
          : 'file',
      url: null,
      preview_url: null,
      name: pending.attachmentFileName,
      mime: pending.uploadedOutgoingAttachments?.[0]?.mime ?? null,
      size: pending.uploadedOutgoingAttachments?.[0]?.size ?? null,
      duration: null,
      provider: 'telegram',
    })
  }

  return {
    id: pending.confirmedServerMessageId ?? 0,
    clientId: pending.clientId,
    conversationId: pending.conversationId,
    direction: 'outgoing',
    text: pending.text,
    createdAt: pending.createdAt,
    isRead: true,
    delivery: {
      status: pending.status === 'error' ? 'failed' : 'pending',
      error: pending.errorText ?? null,
      deliveredAt: null,
    },
    attachments,
    sendState: pending.status === 'error' ? 'error' : 'sending',
    sendError: pending.errorText ?? null,
  }
}

const PENDING_SERVER_MATCH_WINDOW_MS = 2 * 60 * 1000

const getPendingAttachmentKind = (
  pending: CrmIntegrationPendingMessage,
): CrmIntegrationOutgoingAttachment['kind'] | null => {
  const uploadedKind = pending.uploadedOutgoingAttachments?.[0]?.kind
  if (uploadedKind) {
    return uploadedKind
  }

  if (pending.pendingImagePreviewUrl) {
    return 'image'
  }

  if (pending.pendingVideoPreviewUrl) {
    return 'video'
  }

  if (pending.isVoiceMessage || pending.pendingVoicePreviewUrl) {
    return 'voice'
  }

  if (pending.attachmentFileName) {
    return 'file'
  }

  return null
}

const getMessagePrimaryAttachmentKind = (
  message: CrmIntegrationMessageView,
): CrmIntegrationOutgoingAttachment['kind'] | null => {
  const attachment = message.attachments[0]
  if (!attachment) {
    return null
  }

  if (
    attachment.kind === 'image' ||
    attachment.kind === 'file' ||
    attachment.kind === 'video' ||
    attachment.kind === 'audio' ||
    attachment.kind === 'voice' ||
    attachment.kind === 'animation'
  ) {
    return attachment.kind
  }

  return null
}

const findMatchingServerMessageForPending = (
  serverMessages: CrmIntegrationMessageView[],
  pending: CrmIntegrationPendingMessage,
): CrmIntegrationMessageView | null => {
  if (pending.confirmedServerMessageId != null) {
    return (
      serverMessages.find(
        (message) => message.id === pending.confirmedServerMessageId,
      ) ?? null
    )
  }

  const pendingTime = Date.parse(pending.createdAt)
  if (!Number.isFinite(pendingTime)) {
    return null
  }

  const pendingText = pending.text.trim()
  const pendingAttachmentKind = getPendingAttachmentKind(pending)

  for (const message of serverMessages) {
    if (message.conversationId !== pending.conversationId) {
      continue
    }

    if (message.direction !== 'outgoing') {
      continue
    }

    const messageTime = Date.parse(message.createdAt)
    if (
      !Number.isFinite(messageTime) ||
      messageTime < pendingTime - 5_000 ||
      messageTime > pendingTime + PENDING_SERVER_MATCH_WINDOW_MS
    ) {
      continue
    }

    const messageAttachmentKind = getMessagePrimaryAttachmentKind(message)
    if (pendingAttachmentKind !== messageAttachmentKind) {
      continue
    }

    if (pendingText && message.text.trim() !== pendingText) {
      continue
    }

    return message
  }

  return null
}

export const prunePendingAgainstServer = (
  serverMessages: CrmIntegrationMessageView[],
  pendingMessages: CrmIntegrationPendingMessage[],
): CrmIntegrationPendingMessage[] => {
  const next: CrmIntegrationPendingMessage[] = []

  for (const pending of pendingMessages) {
    const match = findMatchingServerMessageForPending(serverMessages, pending)

    if (match != null) {
      // `sent` — провайдер подтвердил, optimistic больше не нужен.
      if (match.delivery.status === 'sent') {
        continue
      }

      // `pending` / `failed` — оставляем локальный ряд (спиннер или retry).
      next.push({
        ...pending,
        confirmedServerMessageId:
          pending.confirmedServerMessageId ?? match.id,
        ...(match.delivery.status === 'failed'
          ? {
              status: 'error' as const,
              errorText:
                pending.errorText ??
                match.delivery.error ??
                CRM_INTEGRATION_SEND_FAILURE_MESSAGE,
            }
          : {}),
      })
      continue
    }

    if (pending.confirmedServerMessageId != null) {
      const stillOnServer = serverMessages.some(
        (message) => message.id === pending.confirmedServerMessageId,
      )
      if (!stillOnServer) {
        next.push(pending)
      }
      continue
    }

    next.push(pending)
  }

  return next
}

/** Pending-ряд заменяет серверный пузырь с тем же id (sending и failed/retry). */
export const isServerMessageHiddenByPending = (
  message: CrmIntegrationMessageView,
  pendingMessages: CrmIntegrationPendingMessage[],
): boolean =>
  pendingMessages.some(
    (pending) =>
      pending.confirmedServerMessageId != null &&
      pending.confirmedServerMessageId === message.id,
  )

export const arePendingMessageListsEqual = (
  left: CrmIntegrationPendingMessage[],
  right: CrmIntegrationPendingMessage[],
): boolean => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((item, index) => {
    const other = right[index]
    return (
      item.clientId === other.clientId &&
      item.status === other.status &&
      item.confirmedServerMessageId === other.confirmedServerMessageId &&
      item.errorText === other.errorText
    )
  })
}
